import { create } from 'zustand';

export type AuthGateView = 'sign-in' | 'sign-up';

interface OpenOptions {
  message?: string;
  /**
   * Which form the sheet opens on. Sign in is right for almost every gate,
   * because almost every gate interrupts someone who already has an account.
   * A caller whose own words invite a NEW account ("Create a free account to
   * save your evaluation") passes 'sign-up', so the button and the form it
   * opens say the same thing.
   */
  initialView?: AuthGateView;
  /**
   * Route to land on after auth completes. Used when the user's onboarding
   * isn't yet complete — the pending callback's `router.push` gets clobbered
   * by the onboarding redirect, so we stash the intent as `returnTo` in the
   * auth store instead and let onboarding navigate there at the end.
   */
  returnPath?: string;
}

interface AuthGateState {
  isOpen: boolean;
  pendingCallback: (() => void) | null;
  pendingReturnPath: string | null;
  message: string | null;
  view: AuthGateView;

  open: (callback: () => void, options?: OpenOptions) => void;
  close: () => void;
  setView: (view: AuthGateView) => void;
  executePending: () => void;
  takeReturnPath: () => string | null;
}

export const useAuthGateStore = create<AuthGateState>((set, get) => ({
  isOpen: false,
  pendingCallback: null,
  pendingReturnPath: null,
  message: null,
  view: 'sign-in',

  open: (callback, options) => {
    set({
      isOpen: true,
      pendingCallback: callback,
      pendingReturnPath: options?.returnPath ?? null,
      message: options?.message ?? null,
      view: options?.initialView ?? 'sign-in',
    });
  },

  close: () => {
    set({ isOpen: false, pendingCallback: null, pendingReturnPath: null, message: null });
  },

  setView: (view) => {
    set({ view });
  },

  executePending: () => {
    const { pendingCallback } = get();
    if (pendingCallback) {
      pendingCallback();
    }
    set({ isOpen: false, pendingCallback: null, pendingReturnPath: null, message: null });
  },

  takeReturnPath: () => {
    const path = get().pendingReturnPath;
    set({ pendingReturnPath: null });
    return path;
  },
}));
